import { useDeferredValue, useEffect, useMemo, useState, type ReactElement } from 'react';
import {
  Alert,
  BatchIcon,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Text,
  TextField,
} from '@citibox-panel/ui';
import { formatCount } from '../lib/coverage';
import { formatParseSummary, parseLocationIds } from '../lib/parseLocationIds';
import { useDoorAccessBatchWrite } from '../hooks/useDoorAccessBatchWrite';
import { BatchConfirmDialog } from './BatchConfirmDialog';
import { neverHandledByHost, type IsHandledByHost } from '../../../lib/hostErrors';

export interface BatchCardProps {
  // The BARE market label, resolved by the host — never the brand pill's `Soporte ES`.
  marketLabel: string | null;
  // Injected by the host, which is what picks mock or real: the slice carries no client and no env
  // wiring, exactly as the counter card's read does.
  setDoorAccess: (locationIds: number[], enabled: boolean) => Promise<void>;
  isHandledByHost?: IsHandledByHost;
}

// The rule LABELS the field rather than trailing it: it has to be read BEFORE anything is pasted,
// and a description below the box is read after. As the label it is also the control's accessible
// name, so it cannot be skipped by an AT user either.
const INPUT_RULE = 'Un ID por línea. También acepta separación por comas.';

// Deliberately NOT the mockup's `LOC-08433` sample: that is exactly what the parser rejects, and a
// sample that would be discarded teaches the wrong format on the one screen where a bad token costs
// a wave of a thousand buildings.
const PLACEHOLDER = '8433\n8452\n8488';

const FIELD_ROWS = 8;

// Fixed panel copy. The server's own `message`/`detail` never reaches the DOM — an error envelope is
// attacker-influenced data like any other payload — and the card switches on no `code` string
// either, because the codes on this route are lowercase while other endpoints send them uppercase.
const WRITE_ERROR = 'No se ha podido completar la escritura.';

// There IS a jump; the panel just does not know its ends. Saying so beats inventing numbers.
const SENT_WITHOUT_DELTA = 'Escritura enviada; el recuento se ha vuelto a leer.';

// The batch write's whole surface: a field that eats a pasted column of ids, a readout of what the
// panel will actually send, and the two directions of the gate.
//
// TODO: i18n — copy en ES hasta Fase 1.
export function BatchCard({
  marketLabel,
  setDoorAccess,
  isHandledByHost = neverHandledByHost,
}: BatchCardProps): ReactElement {
  const [raw, setRaw] = useState('');
  // `null` is «no modal»; the value is also which direction is being confirmed.
  const [intent, setIntent] = useState<'enable' | 'disable' | null>(null);
  const { write, isWriting, isError, error, delta, hasWritten } =
    useDoorAccessBatchWrite(setDoorAccess);
  // A refusal the host already answers with its own screen gets no alert here. The modal below
  // still closes on the raw `isError`: the write did settle, whoever reports it.
  const showsWriteError = isError && !isHandledByHost(error);

  // Deferred so a paste of thousands does not re-parse on every keystroke; the counts are DERIVED
  // on every render rather than stored, so they cannot drift from the text they describe.
  const deferredRaw = useDeferredValue(raw);
  const parsed = useMemo(() => parseLocationIds(deferredRaw), [deferredRaw]);
  const summary = formatParseSummary(parsed);

  const hasBatch = parsed.ids.length > 0;

  // The modal closes when the write SETTLES, not when it is confirmed, so focus returns to a card
  // button that is enabled again rather than to one MUI disabled for the duration. Reopening still
  // works: these deps do not change when a new confirmation opens.
  useEffect(() => {
    if (!isWriting && (hasWritten || isError)) {
      setIntent(null);
    }
  }, [isWriting, hasWritten, isError]);

  // The counter jump is the only receipt an endpoint answering 200 with an empty body permits.
  // Empty until something has been written — including on the failure path, where the alert speaks
  // instead.
  const receipt = !hasWritten
    ? ''
    : delta === null
      ? SENT_WITHOUT_DELTA
      : `Activadas: ${formatCount(delta.before)} → ${formatCount(delta.after)}`;

  return (
    // Elevated at level 4, matching the counter card beside it: both sit ON the page's white panel,
    // where a hairline border would vanish into the surface behind it.
    <Card variant="elevation" elevation={4} sx={{ borderRadius: 3 }}>
      <CardContent>
        {/* Half a step, against the full one the façade puts BELOW the rule: the sentence is bound
            tighter to the heading it continues than to the box it describes, so it reads as the
            heading's second line first and as the field's instruction second. An even gap on both
            sides would leave it floating between the two, belonging to neither. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <BatchIcon fontSize="small" />
          {/* `component="h2"` is what puts it in the document outline: with several cards on the
              page, heading navigation is how an AT user moves between them. */}
          <Text variant="subtitle2" component="h2" bold>
            Activación por lote
          </Text>
        </Box>
        <TextField
          label={INPUT_RULE}
          value={raw}
          onChange={setRaw}
          multiline
          rows={FIELD_ROWS}
          monospace
          fullWidth
          placeholder={PLACEHOLDER}
          // The helper slot is the READOUT's alone. Omitted rather than emptied while there is
          // nothing to report: an empty helper still mints a node and points `aria-describedby` at
          // it, so the field would advertise a description that says nothing.
          {...(summary !== '' ? { helperText: summary } : {})}
        />
        {/* No `onKeyDown`: Enter inserts a newline, because it is the separator. The write has
            exactly one trigger, and it is these two buttons. */}
        {/* `loading` as well as `disabled`, so a second wave cannot be started from behind the
            modal while the first is still in flight. */}
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            disabled={!hasBatch}
            loading={isWriting}
            onClick={() => {
              setIntent('enable');
            }}
          >
            ACTIVAR
          </Button>
          {/* The destructive direction is error-coloured on top of its word, never instead of it:
              the mockup drew the two as equal-weight siblings, which made the irreversible one look
              like the cautious choice. */}
          <Button
            variant="outlined"
            color="error"
            disabled={!hasBatch}
            loading={isWriting}
            onClick={() => {
              setIntent('disable');
            }}
          >
            DESACTIVAR
          </Button>
        </Stack>
        {/* Mounted in every state, so the region exists BEFORE its content changes — a region that
            arrives with its text is the update assistive tech routinely misses. */}
        <Box role="status" sx={{ mt: 2 }}>
          {receipt !== '' && (
            <Text variant="body2" bold>
              {receipt}
            </Text>
          )}
        </Box>
        {/* Outside the region, which stays empty here: an alert carries its own semantics and must
            not be paraphrased by a status region beside it. */}
        {showsWriteError && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="error">{WRITE_ERROR}</Alert>
          </Box>
        )}
      </CardContent>
      <BatchConfirmDialog
        open={intent !== null}
        intent={intent ?? 'enable'}
        count={parsed.ids.length}
        marketLabel={marketLabel}
        isWriting={isWriting}
        onConfirm={() => {
          // The DESTRUCTIVE direction has to be asked for by name. `intent === 'enable'` would send
          // `false` — disable — for anything that is not exactly `'enable'`, including the `null`
          // this dialog cannot currently be confirmed under. Unreachable today; on the panel's only
          // irreversible write the safe direction is the one worth making the default.
          write(parsed.ids, intent !== 'disable');
        }}
        onCancel={() => {
          setIntent(null);
        }}
      />
    </Card>
  );
}
